import React, { useState } from 'react';
import { A } from 'hookrouter';
import { useForm } from 'hooks';
import { checkRequiredFields, ErrorChecklist } from 'common';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import InputGroup from 'react-bootstrap/InputGroup';
import showIcon from 'images/show.png';
import hideIcon from 'images/hide.png';
import './signupForm.scss';

const SignupForm = ({ signUp }) => {
  const requiredFields = ['firstName', 'lastName', 'phone', 'email', 'password'];
  const [maskPassword, setMaskPassword] = useState(true);

  const signUpFormValidationRules = (values) => {
    let errors = {};

    if (values.firstName.length < 1) {
      errors.firstName = 'First name required';
    }
    if (values.lastName.length < 1) {
      errors.lastName = 'Last name required';
    }

    if (
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        values.email
      )
    ) {
      errors.email = 'Invalid email address';
    }

    if (values.phone.length < 1) {
      errors.phone = 'Phone required';
    } else if (values.phone.length !== 14) {
      //number of characters after formatting 10
      errors.phone = 'Invalid phone number';
    }

    if (
      !/^.{8,30}$/.test(values.password) ||
      !/[A-Z]+/.test(values.password) ||
      !/[a-z]+/.test(values.password) ||
      !/\d+/.test(values.password) ||
      values.password.match(new RegExp(values.email, 'i'))
    ) {
      errors.password = 'Invalid password';
    }
    return errors;
  };

  const passwordValidationRules = [
    ['8-30 characters', (values) => /^.{8,30}$/.test(values.password)],
    ['at least one upper case character', (values) => /[A-Z]+/.test(values.password)],
    ['at least one lower case character', (values) => /[a-z]+/.test(values.password)],
    ['at least one number', (values) => /\d+/.test(values.password)],
    [
      'cannot have email in password',
      (values) => values.email && !values.password.match(new RegExp(values.email, 'i')),
    ],
  ];

  const defaultValues = { firstName: '', lastName: '', email: '', phone: '', password: '' };
  const { values, handleChange, handleBlur, errors } = useForm(defaultValues, signUpFormValidationRules);

  // const formatPhoneNumber = value => {
  //   if (!value) return '';
  //   // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s02.html
  //   const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  //   return value.replace(/[^0-9]/g, '').replace(phoneRegex, "($1) $2-$3");
  // };

  const phoneNumberFormat = (event) => {
    let {
      target: { value: phoneNumber },
    } = event;

    // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s02.html
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

    event.target.value = phoneNumber.replace(/[^0-9]/g, '').replace(phoneRegex, '($1) $2-$3');
    handleChange(event);
  };

  return (
    <div className="occu-form occu-form-wide">
      <h4>CREATE APPLICATION LOGIN</h4>
      <Form
        name="signupForm"
        id="signupForm"
        onSubmit={(event) => {
          signUp(values);
          event.preventDefault();
        }}
      >
        <Form.Group controlId="formFirstName">
          <Form.Label>First</Form.Label>
          <Form.Control
            type="text"
            name="firstName"
            value={values.firstName}
            onBlur={handleBlur}
            onChange={(event) => {
              event.target.value = event.target.value.replace(/[^A-Za-z]/g, '');
              handleChange(event);
            }}
            isInvalid={errors.firstName}
            autoComplete="off"
            maxLength="20"
            required
          />
          <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="formLastName">
          <Form.Label>Last</Form.Label>
          <Form.Control
            type="text"
            name="lastName"
            value={values.lastName}
            onBlur={handleBlur}
            onChange={(event) => {
              event.target.value = event.target.value.replace(/[^A-Za-z]/g, '');
              handleChange(event);
            }}
            isInvalid={errors.lastName}
            autoComplete="off"
            maxLength="20"
            required
          />
          <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="formEmail">
          <Form.Label className="form-label">Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={errors.email}
            autoComplete="off"
            maxLength="40"
            required
          />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="formPhone">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            name="phone"
            value={values.phone}
            onBlur={handleBlur}
            onChange={phoneNumberFormat}
            isInvalid={errors.phone}
            autoComplete="off"
            maxLength="14"
            required
          />
          <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="formPassword">
          <Form.Label>Password</Form.Label>
          <InputGroup>
            <Form.Control
              name="password"
              type={maskPassword ? 'password' : 'text'}
              value={values.password}
              onBlur={handleBlur}
              onChange={handleChange}
              isInvalid={errors.password}
              autoComplete="off"
              maxLength="30"
              required
            />
            <InputGroup.Append>
              <Button
                variant="light"
                onClick={() => {
                  setMaskPassword(!maskPassword);
                }}
              >
                <div style={{ height: 20, width: 20 }}>
                  {maskPassword ? <Image src={showIcon} fluid /> : <Image src={hideIcon} fluid />}
                </div>
              </Button>
            </InputGroup.Append>
          </InputGroup>
          <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <ErrorChecklist rules={passwordValidationRules} {...values} />
        </Form.Group>
        <Form.Text>
          By selecting the Submit button, you agree to receive a call(s), text(s), or email(s) regarding an Orange
          County's Credit Union home loan. Data rates may apply.
        </Form.Text>
        <br />
        <br />
        <Form.Group controlId="formSubmit">
          <Button
            variant="occu-primary"
            type="submit"
            block
            disabled={
              !checkRequiredFields(requiredFields, values) ||
              (Object.entries(values).length === 0 && values.constructor === Object) ||
              (Object.entries(errors).length > 0 && errors.constructor === Object)
            }
          >
            Submit
          </Button>
        </Form.Group>
      </Form>
      <h5>
        Already enrolled? <br />
        Click <A href="/mortgage/sign-in">here</A>
      </h5>
    </div>
  );
};

export default SignupForm;
